import type { QueryFunctionContext } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { requestWithAuth } from '@/utilities/graphQLClient';

type NarrativePermissions = {
  canAddAnnouncement: boolean;
  canDeleteAnnouncement: boolean;
  canUpdateAnnouncement: boolean;
};

export type UserVotedNarratives = Array<{
  narrativeId: string;
  voteType: 1 | -1;
}>;

export interface QueryResponse {
  errors?: Error[];
  NarrativePermissions: NarrativePermissions;
  UserVotedNarratives: UserVotedNarratives;
}

export interface PermissionResponse {
  narrativePermissions: NarrativePermissions;
  userVotedNarratives: UserVotedNarratives;
}

type QueryContext = QueryFunctionContext<[string, string | undefined]>;

export const queryFn = async ({
  queryKey: [_, userId],
}: QueryContext): Promise<PermissionResponse> => {
  if (typeof userId === 'undefined') {
    return Promise.reject(new Error('User ID must be provided'));
  }
  const { NarrativePermissions, UserVotedNarratives, errors } =
    await requestWithAuth<QueryResponse>(gql`
      query NarrativePermission {
        NarrativePermissions {
          canAddAnnouncement
          canDeleteAnnouncement
          canUpdateAnnouncement
        }
        UserVotedNarratives {
          narrativeId
          voteType
        }
      }
    `);

  if (errors?.length) {
    const [firstError] = errors;
    throw new Error(firstError.message);
  }

  return {
    narrativePermissions: NarrativePermissions,
    userVotedNarratives: UserVotedNarratives,
  };
};
